import type { AnyAction, Dispatch } from 'redux';

import type {
  GridsCheckType,
  GridType,
  GridState,
  BasketState,
  HistoryFilter,
  PublicGridFilter,
  GridMatchLength,
} from '..';
import { type ObjectValuesUnionType } from '../../../../utils/typing';

export const GridsActionTypes = Object.freeze({
  INIT: 'grids/init',

  ASK_POOL: 'grids/pools/ask',

  COPY_GRID: 'grids/basket/copyGrid',
  INIT_BASKET: 'grids/basket/init',
  INIT_PUBLIC_BASKET: 'grids/basket/initPublic',
  INIT_HISTORY_BASKET: 'grids/basket/initHistory',
  FILL_PRECHECKS: 'grids/basket/prechecks',
  CLEAR_BASKET: 'grids/basket/clear',
  SET_BASKET_PENDING: 'grids/basket/setPending',
  SET_BASKET_VALIDATED: 'grids/basket/setValidated',
  SET_BASKET_ERROR: 'grids/basket/setError',
  SUBMIT_BASKET: 'grids/basket/submit',
  CHECK: 'grids/basket/check',
  CLEAR_CHECKS: 'grids/basket/clearChecks',
  SET_STAKE: 'grids/basket/setStake',
  FILL_RANDOMLY: 'grids/basket/fillRandomly',
  SWITCH_TYPE: 'grids/basket/switchType',
  TOGGLE_PRIVATE_MODE: 'grids/basket/togglePrivate',
  TOGGLE_SYSTEM_MODE: 'grids/basket/toggleSystem',
  REMOVE_SYSTEM_GRID: 'grids/basket/removeSystemGrid',
  COPY_SYSTEM_GRID: 'grids/basket/copySystemGrid',

  SET_HISTORY_GRID_COUNT: 'grids/history/setGridCount',
  SET_HISTORY_FETCHING: 'grids/history/setFetching',
  SET_HISTORY_PAGE: 'grids/history/setPage',
  SET_HISTORY_FILTER: 'grids/history/setGridFilter',
  ASK_HISTORY_GRIDS: 'grids/history/askGrids',
  ASK_HISTORY_GRID: 'grids/history/askGrid',
  SET_HISTORY_REFERENCES: 'grids/history/setReferences',

  ASK_PUBLIC_GRIDS: 'grids/public/ask',
  SET_PUBLIC_GRID_PAGE: 'grids/public/setGridPage',
  SET_PUBLIC_GRID_FILTER: 'grids/public/setGridFilter',
  SET_PUBLIC_POOL_ID: 'grids/public/setPoolId',

  SET_TUTO_STEP: 'grids/ui/setTutoStep',
  SET_POOL_NAVIGATION: 'grids/ui/setPoolNavigation',
  SET_ACTIVE_POOLS: 'grids/ui/setActivePools',
  SET_RUNNING_GRIDS: 'grids/ui/setRunningGrids',
});

export type GridsActionType = ObjectValuesUnionType<typeof GridsActionTypes>;

export type OnlyTypeAction = { type: GridsActionType };

export type OnCheckBoxAction = {
  type: GridsActionType;
  payload: { matchIndex: number; checkType: GridsCheckType; selectionIndex: number };
};
export type OnCheckBoxType = (
  matchIndex: number,
  checkType: GridsCheckType,
  selectionIndex: number,
) => OnCheckBoxAction;

export type UpdateBasketAction = { type: GridsActionType; payload: { basket: BasketState } };
export type UpdateBasketType = (basket: BasketState) => UpdateBasketAction;

export type InitBasketAction = {
  type: GridsActionType;
  payload: { poolId: number; selections: Array<string> };
};
export type InitBasketType = (props: { poolId: number; selections: Array<string> }) => InitBasketAction;

export type InitPublicBasketAction = {
  type: GridsActionType;
  payload: { poolId: number; gridReference: string };
};
export type InitPublicBasketType = ({
  poolId,
  gridReference,
}: {
  poolId: number;
  gridReference: string;
}) => InitPublicBasketAction;

export type InitHistoryBasketAction = {
  type: GridsActionType;
  payload: { poolId: number; gridReference: string };
};
export type InitHistoryBasketType = ({
  poolId,
  gridReference,
}: {
  poolId: number;
  gridReference: string;
}) => InitHistoryBasketAction;

export type FillPrechecksAction = {
  type: GridsActionType;
  payload: { poolId: number; selections: Array<string> };
};
export type FillPrechecksType = (props: { poolId: number; selections: Array<string> }) => FillPrechecksAction;

export type CopyGridAction = {
  type: GridsActionType;
  payload: { grid: GridState };
};
export type CopyGridType = (grid: GridState) => CopyGridAction;

export type SetBasketValidatedAction = { type: GridsActionType; payload: { gridReference: string } };
export type SetBasketValidatedType = ({ gridReference }: { gridReference: string }) => SetBasketValidatedAction;

export type SetBasketClearType = () => OnlyTypeAction;

export type SetTutoStepAction = { type: GridsActionType; payload: { step: number } };
export type SetTutoStepType = (step: number) => SetTutoStepAction;

export type SetPoolNavigationAction = { type: GridsActionType; payload: { type: GridMatchLength } };
export type SetPoolNavigationType = (type: GridMatchLength) => OnlyTypeAction;

export type SetActivePoolsAction = { type: GridsActionType; payload: { activePools: Array<number> } };
export type SetActivePoolsType = (activePools: Array<number>) => SetActivePoolsAction;

export type SetRunningGridsAction = { type: GridsActionType; payload: { runningGrids: Array<string> } };
export type SetRunningGridsType = (runningGrids: Array<string>) => SetRunningGridsAction;

export type SetHistoryReferencesAction = { type: GridsActionType; payload: { gridReferences: Array<string> } };
export type SetHistoryReferencesType = (gridReferences: Array<string>) => SetHistoryReferencesAction;

export type SetBasketErrorAction = { type: GridsActionType; payload: { estr: string } };
export type SetBasketErrorType = (estr: string) => SetBasketErrorAction;

export type SetBasketPendingType = () => OnlyTypeAction;

export type SubmitBasketType = () => (dispatch: Dispatch<AnyAction>, getState: unknown) => void;

export type ClearChecksAction = { type: GridsActionType; payload: { index: number } };
export type ClearChecksType = (index: number) => ClearChecksAction;

export type FillRandomlyAction = { type: GridsActionType; payload: { selectionIndex: number } };
export type FillRandomlyType = (selectionIndex: number) => FillRandomlyAction;

export type CopySystemGridAction = { type: GridsActionType; payload: { index: number } };
export type CopySystemGridType = (index: number) => CopySystemGridAction;

export type ToggleSystemGridType = () => OnlyTypeAction;

export type TogglePrivateGridType = () => OnlyTypeAction;

export type RemoveSystemGridAction = { type: GridsActionType; payload: { index: number } };
export type RemoveSystemGridType = (index: number) => RemoveSystemGridAction;

export type SetStakeAction = { type: GridsActionType; payload: { betAmount: string } };
export type SetStakeType = (betAmount: string) => SetStakeAction;

export type SwitchTypeAction = { type: GridsActionType; payload: { gridType: GridType } };
export type SwitchTypeType = (gridType: GridType) => SwitchTypeAction;

export type SetHistoryPageAction = { type: GridsActionType; payload: { currentPage: number } };
export type SetHistoryPageType = (currentPage: number) => SetHistoryPageAction;

export type SetHistoryFilterAction = { type: GridsActionType; payload: { filter: HistoryFilter } };
export type SetHistoryFilterType = (filter: HistoryFilter) => SetHistoryFilterAction;

export type SetHistoryPrevPageType = (
  currentPage: number,
) => (dispatch: Dispatch<AnyAction>, getState: unknown) => void;

export type SetHistoryNextPageType = (
  currentPage: number,
) => (dispatch: Dispatch<AnyAction>, getState: unknown) => void;

export type SetHistoryFetchingAction = { type: GridsActionType; payload: { isFetching: boolean } };
export type SetHistoryFetchingType = (isFetching: boolean) => SetHistoryFetchingAction;

export type SetHistoryGridCountAction = { type: GridsActionType; payload: { gridCount: number } };
export type SetHistoryGridCountType = (gridCount: number) => SetHistoryGridCountAction;

export type SetPublicGridPageAction = { type: GridsActionType; payload: { currentPage: number } };
export type SetPublicGridPageType = (currentPage: number) => SetPublicGridPageAction;

export type SetPublicGridFilterAction = { type: GridsActionType; payload: { filter: PublicGridFilter } };
export type SetPublicGridFilterType = (filter: PublicGridFilter) => SetPublicGridFilterAction;

export type SetPublicPoolIdAction = { type: GridsActionType; payload: { poolId: number } };
export type SetPublicPoolIdType = (poolId: number) => SetPublicPoolIdAction;

export type AskHistoryGridAction = { type: GridsActionType; payload: { gridReference: string } };
export type AskHistoryGridType = (gridReference: string) => AskHistoryGridAction;

export type AskHistoryGridsAction = { type: GridsActionType; payload: { offset: number } };
export type AskHistoryGridsType = (offset: number) => AskHistoryGridsAction;

export type AskPublicGridsAction = { type: GridsActionType; payload: { poolId: number } };
export type AskPublicGridsType = (poolId: number) => AskPublicGridsAction;

export type AskPoolAction = { type: GridsActionType; payload: { poolId: number } };
export type AskPoolType = (poolId: number) => AskPoolAction;
